import {queries} from '@/shared/integrations/zero'
import {Ward} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

export type WardStartRow = Pick<Ward, 'id' | 'createdAt' | 'name'>

export function useGetInfiniteWards({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit = 25,
}: Omit<Partial<Parameters<typeof queries.locations.listWards>[0]>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [sort?.dir, sort?.field, filters?.locationId, filters?.sectionId, filters?.name, limit, search]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getPageQuery: GetPageQuery<Ward, WardStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {
              id: start.id,
              direction: dir,
              createdAt: start.createdAt,
              name: start.name,
            }
          : undefined

      return {
        query: queries.locations.listWards({
          cursor,
          limit: pageLimit,
          sort,
          filters: {status: ['active'] as const, ...filters, ...(search ? {name: search} : {})},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<Ward> = useCallback(
    ({id}) => ({
      query: queries.locations.getWardById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: Ward): WardStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
    }),
    []
  )

  const transformToSelectItem = useCallback((row: Ward) => ({label: row.name, value: row.id}), [])

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
